import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, User } from "lucide-react";
import { useBusiness } from "../../../context/BusinessContext";

interface HeaderProps {
  hideSearch?: boolean;
  hideBusinessSelector?: boolean;
  isChatOpen?: boolean;
  isChatMaximized?: boolean;
}

export function Header({ hideBusinessSelector = false, isChatOpen = false, isChatMaximized = false }: HeaderProps) {
  const navigate = useNavigate();
  const { businesses, selected, setSelected } = useBusiness();
  const [bizDropdownOpen, setBizDropdownOpen] = useState(false);
  const bizRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (bizRef.current && !bizRef.current.contains(event.target as Node)) {
        setBizDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectBusiness = (b: typeof businesses[0]) => {
    setSelected(b);
    setBizDropdownOpen(false);
    navigate("/deals");
  };

  return (
    <div className="px-8 py-2 w-full">
      <div className="max-w-[1440px] mx-auto w-full flex items-center justify-end gap-3">
        {/* Business Selector - Main App Style */}
        {!hideBusinessSelector && (
          <div className="flex items-center gap-3">
            <div className="relative" ref={bizRef}>
              <button
                className="flex items-center gap-2 border shadow-sm py-3 px-4 rounded bg-white hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                onClick={() => setBizDropdownOpen(!bizDropdownOpen)}
              >
                <span>{selected?.name || "Select business"}</span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {bizDropdownOpen && (
                <div className="absolute top-12 right-0 w-64 bg-white border rounded shadow-sm z-50 overflow-hidden">
                  {businesses.map((b) => (
                    <button
                      key={b.slug}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-100 truncate ${selected?.slug === b.slug ? "bg-gray-50 font-semibold" : "text-gray-700"
                        }`}
                      onClick={() => handleSelectBusiness(b)}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Global Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              className="border shadow-sm py-3 px-4 rounded text-sm bg-indigo-500 text-white hover:bg-indigo-600 transition-colors font-semibold"
            >
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
